import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, PlusCircle, History, Users, LogOut, Cog, Menu, X, Wrench, CalendarClock, Warehouse, Building2, Zap, ShoppingCart, ClipboardList } from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/nuovo", label: "Nuovo Montaggio", icon: PlusCircle, testid: "nav-nuovo" },
  { to: "/macchine", label: "Macchine", icon: Wrench, testid: "nav-macchine" },
  { to: "/impianti", label: "Impianti", icon: Building2, testid: "nav-impianti" },
  { to: "/pianificazione", label: "Pianificazione", icon: CalendarClock, testid: "nav-pianificazione" },
  { to: "/storico", label: "Storico", icon: History, testid: "nav-storico" },
  { to: "/magazzino", label: "Magazzino", icon: Warehouse, testid: "nav-magazzino", warehouseOnly: true },
  { to: "/riordini", label: "Report Riordini", icon: ShoppingCart, testid: "nav-riordini", warehouseOnly: true },
  { to: "/ordini", label: "Storico Ordini", icon: ClipboardList, testid: "nav-ordini", adminOnly: true },
  { to: "/elettrici", label: "Componenti Elettrici", icon: Zap, testid: "nav-elettrici" },
  { to: "/anagrafica", label: "Anagrafica", icon: Users, testid: "nav-anagrafica", adminOnly: true },
];

function Clock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <span className="font-mono-data text-sm text-slate-400 tabular-nums" data-testid="factory-clock">
      {now.toLocaleTimeString("it-IT")}
    </span>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const items = NAV.filter((n) => {
    if (n.adminOnly && user?.role !== "admin") return false;
    if (n.warehouseOnly && !(user?.role === "admin" || user?.warehouse_access)) return false;
    return true;
  });

  const SideNav = (
    <nav className="flex flex-col gap-1 p-3">
      {items.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.to === "/"}
          data-testid={n.testid}
          onClick={() => setOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
              isActive
                ? "bg-amber-500/15 text-amber-400 border border-amber-500/30"
                : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/60 border border-transparent"
            }`
          }
        >
          <n.icon className="h-[18px] w-[18px]" />
          {n.label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100">
      {/* Header */}
      <header className="sticky top-0 z-40 h-16 border-b border-slate-800 bg-slate-900/95 backdrop-blur-md flex items-center px-4 sm:px-6 gap-4">
        <button
          className="lg:hidden text-slate-300"
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X /> : <Menu />}
        </button>
        <div className="flex items-center gap-2.5" data-testid="nav-logo">
          <div className="h-9 w-9 rounded-lg bg-amber-500 flex items-center justify-center">
            <Cog className="h-5 w-5 text-slate-950" />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-extrabold text-lg tracking-tight">MEKANIKA</div>
            <div className="text-[10px] font-mono-data uppercase tracking-widest text-slate-500">
              Assembly Tracker
            </div>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-4">
          <span className="hidden sm:block"><Clock /></span>
          <div className="flex items-center gap-2.5">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium leading-tight" data-testid="current-user-name">
                {user?.name}
              </div>
              <div className="text-[11px] uppercase font-mono-data tracking-wider text-amber-400">
                {user?.role === "admin" ? "Responsabile" : "Operatore"}
              </div>
            </div>
            <div className="h-9 w-9 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-sm font-semibold text-amber-400">
              {user?.name?.[0]?.toUpperCase() || "?"}
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="text-slate-400 hover:text-rose-400 transition-colors"
            title="Esci"
          >
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </header>

      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="hidden lg:block w-64 shrink-0 border-r border-slate-800 min-h-[calc(100vh-4rem)] bg-slate-900/40">
          {SideNav}
        </aside>

        {/* Mobile drawer */}
        {open && (
          <div className="lg:hidden fixed inset-0 z-30 top-16">
            <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
            <aside className="absolute left-0 top-0 bottom-0 w-64 bg-slate-900 border-r border-slate-800">
              {SideNav}
            </aside>
          </div>
        )}

        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
